/**
 * The CSV import flow.
 *
 * Two steps, because the second one is a decision nobody should make by
 * accident: pick the file and map its columns, then say what consent these
 * addresses carry. There is no default for that, and the screen says why.
 */

import { useEffect, useRef, useState } from "react";

import { api, ApiError, orgHeader } from "../api/client";
import { Metric } from "../components/Metric";
import { Progress } from "../components/Loading";
import { Icon } from "../components/Sprites";
import type { ConsentStatus, ContactImport, CustomField } from "../api/types";

interface ImportDialogProps {
  onClose: () => void;
  onFinished: () => void;
}

/** Field names on the left, CSV column names on the right. */
interface ColumnMapping {
  email: string;
  name: string;
  /** The column holding each row's own consent status. Never guessed. */
  status: string;
  /** What each value in that column means. */
  status_values: Record<string, ConsentStatus>;
  consent_at: string;
  unsubscribed_at: string;
  attributes: Record<string, string>;
  tags: string[];
}

export function ImportDialog({ onClose, onFinished }: ImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({
    email: "",
    name: "",
    status: "",
    status_values: {},
    consent_at: "",
    unsubscribed_at: "",
    attributes: {},
    tags: [],
  });
  const [scan, setScan] = useState<StatusScan | null>(null);
  // Bumped on every new file or status column, so a scan of a large file that
  // finishes after the choice has moved on is dropped rather than applied.
  const scanSeq = useRef(0);
  const [consent, setConsent] = useState<ConsentStatus | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [started, setStarted] = useState<ContactImport | null>(null);

  const [fields, setFields] = useState<CustomField[]>([]);
  useEffect(() => {
    api
      .get<CustomField[]>("/v1/custom-fields")
      .then(setFields)
      .catch(() => setFields([]));
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  /**
   * Reads the header row in the browser so the mapping can be built from the
   * real column names. Only the first chunk is read — the file may be fifty
   * megabytes and the header is in the first line of it.
   */
  const chooseFile = async (chosen: File) => {
    setFile(chosen);
    setError(null);

    const head = await chosen.slice(0, 64 * 1024).text();
    const firstLine = head.split(/\r?\n/)[0] ?? "";
    const columns = parseHeaderRow(firstLine);
    setHeaders(columns);

    // A first guess, by name. Convenience only — nothing is guessed about
    // consent, which is the field that matters.
    const guess = (candidates: string[]) =>
      columns.find((column) => candidates.includes(column.trim().toLowerCase())) ?? "";

    setMapping({
      email: guess(["email", "email address", "e-mail", "mail"]),
      name: guess(["name", "full name", "contact name"]),
      // Guessed like the others, and for a better reason than convenience: an
      // unmapped date column is not an empty field, it is every contact in the
      // file stamped with the moment of the import. Somebody migrating a list
      // does not know that column matters until it is already lost.
      //
      // The candidates are what real exports call it. That is not a vendor
      // coupling — a wrong guess costs a dropdown change, and the mapping is
      // still by column name, so a platform nobody thought of works by picking
      // it manually.
      consent_at: guess([
        "optin_time",
        "opt-in time",
        "confirm_time",
        "consent_at",
        "subscribed_at",
        "signup date",
        "date added",
      ]),
      // Guessed for the consent date's reason: left unmapped, every opt-out in
      // the file is recorded as happening today.
      unsubscribed_at: guess(["unsub_time", "unsubscribed_at"]),
      // Never guessed, unlike everything around it. A column that happens to
      // be called "status" and holds something other than consent would put a
      // whole list on a status nobody chose.
      status: "",
      status_values: {},
      attributes: {},
      tags: [],
    });
    scanSeq.current++;
    setScan(null);
  };

  /**
   * Reads every value the chosen status column holds, so each can be given a
   * meaning before the import starts.
   *
   * The whole file rather than the chunk the header came from: a value that
   * first appears on row 40,000 would otherwise have no control here, and its
   * rows could only be reported as not imported.
   */
  const chooseStatusColumn = async (column: string) => {
    const seq = ++scanSeq.current;
    setMapping((m) => ({ ...m, status: column, status_values: {} }));
    if (!column || !file) {
      setScan(null);
      return;
    }

    setScan({ reading: true, values: [], tooMany: false });
    const { values, tooMany } = distinctValues(await file.text(), column, MAX_STATUS_VALUES);
    if (seq !== scanSeq.current) return;

    // A value that is already a status name is read as itself by the server
    // too; writing it into the mapping makes the request say so outright.
    const known: Record<string, ConsentStatus> = {};
    for (const value of values) {
      const lower = value.toLowerCase();
      if (isConsentStatus(lower)) known[value] = lower;
    }
    setMapping((m) => ({ ...m, status_values: known }));
    setScan({ reading: false, values, tooMany });
  };

  const submit = async () => {
    if (!file || !consent) return;

    setBusy(true);
    setError(null);

    const body = new FormData();
    body.append("file", file);
    body.append("consent_status", consent);
    body.append("mapping", JSON.stringify(mapping));

    try {
      // FormData rather than the JSON client: the browser has to set the
      // multipart boundary, and it will not if a Content-Type is supplied.
      const response = await fetch("/v1/contacts/import", {
        method: "POST",
        headers: orgHeader(),
        credentials: "same-origin",
        body,
      });

      if (!response.ok) {
        const problem = await response.json().catch(() => null);
        throw new Error(problem?.detail ?? problem?.title ?? `Upload failed (${response.status})`);
      }

      setStarted((await response.json()) as ContactImport);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
      setBusy(false);
    }
  };

  return (
    <>
      <div className="oc-drawer-scrim" onClick={onClose} />
      <aside className="oc-drawer" role="dialog" aria-modal="true" aria-labelledby="import-title">
        <header className="oc-drawer__header">
          <div>
            <div className="oc-label" style={{ marginBottom: 8 }}>
              Contacts
            </div>
            <h2 className="oc-headline-md" id="import-title">
              Import a CSV
            </h2>
          </div>
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--icon oc-btn--sm"
            aria-label="Close"
            onClick={onClose}
          >
            <Icon name="close" className="oc-nav__icon" />
          </button>
        </header>

        <div className="oc-drawer__body">
          {/* Two phases, and once the second starts the first is gone for good —
              an import cannot be un-started. Saying so up front is the point:
              the upload step is where consent is declared, and someone who
              expects to go back and change it should find out before, not
              after. */}
          <ol className="oc-steps" style={{ marginBottom: "var(--oc-space-4)" }}>
            <li className="oc-steps__step" data-state={started ? "done" : "current"}>
              <span className="oc-steps__mark" aria-hidden="true">
                {started ? "✓" : "1"}
              </span>
              File and consent
            </li>
            <span className="oc-steps__bar" aria-hidden="true" />
            <li className="oc-steps__step" data-state={started ? "current" : undefined}>
              <span className="oc-steps__mark" aria-hidden="true">
                2
              </span>
              Import
            </li>
          </ol>

          {started ? (
            <ImportProgress
              importId={started.id}
              onDone={() => {
                onFinished();
              }}
              onClose={onClose}
            />
          ) : (
            <>
              <section className="drawer-section">
                <span className="oc-label">File</span>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="oc-input"
                  style={{ height: "auto", padding: "var(--oc-space-2)" }}
                  onChange={(event) => {
                    const chosen = event.target.files?.[0];
                    if (chosen) void chooseFile(chosen);
                  }}
                />
                {file && (
                  <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
                    {file.name} · {(file.size / 1024).toFixed(0)} KB · {headers.length} columns
                  </p>
                )}
              </section>

              {headers.length > 0 && (
                <section className="drawer-section">
                  <span className="oc-label">Columns</span>
                  <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
                    Matched by name, not position — a spreadsheet exported twice rarely has its
                    columns in the same order.
                  </p>

                  <MapRow
                    label="Email address"
                    required
                    value={mapping.email}
                    headers={headers}
                    onChange={(value) => setMapping((m) => ({ ...m, email: value }))}
                  />
                  <MapRow
                    label="Name"
                    value={mapping.name}
                    headers={headers}
                    onChange={(value) => setMapping((m) => ({ ...m, name: value }))}
                  />
                  <MapRow
                    label="Consent status"
                    value={mapping.status}
                    headers={headers}
                    onChange={(value) => void chooseStatusColumn(value)}
                  />
                  {mapping.status && scan && (
                    <StatusValueMap
                      scan={scan}
                      chosen={mapping.status_values}
                      onChoose={(value, status) =>
                        setMapping((m) => {
                          const status_values = { ...m.status_values };
                          if (status) status_values[value] = status;
                          else delete status_values[value];
                          return { ...m, status_values };
                        })
                      }
                    />
                  )}
                  <MapRow
                    label="Consent date"
                    value={mapping.consent_at}
                    headers={headers}
                    onChange={(value) => setMapping((m) => ({ ...m, consent_at: value }))}
                  />
                  {/*
                    Said here rather than in a tooltip, because the cost of not
                    mapping it is invisible until somebody needs the trail and
                    finds that everybody agreed on the same afternoon.
                  */}
                  <p className="oc-body-sm oc-text-secondary" style={{ marginTop: "var(--oc-space-2)" }}>
                    {mapping.consent_at ? (
                      <>
                        Each subscribed or pending contact keeps the date they originally agreed. Use{" "}
                        <span className="oc-mono-sm">YYYY-MM-DD</span> or a full timestamp —{" "}
                        <span className="oc-mono-sm">03/04/2019</span> is refused, because it is 3
                        April in most of the world and 4 March in the US and guessing wrong writes a
                        consent date that never happened.
                      </>
                    ) : (
                      <>
                        Optional, and worth finding if the file has one. Without it every contact is
                        recorded as having consented <em>today</em>, so a list migrated from another
                        platform loses the dates that make its consent defensible.
                      </>
                    )}
                  </p>

                  {(mapping.status || consent === "unsubscribed") && (
                    <>
                      <MapRow
                        label="Opt-out date"
                        value={mapping.unsubscribed_at}
                        headers={headers}
                        onChange={(value) => setMapping((m) => ({ ...m, unsubscribed_at: value }))}
                      />
                      <p className="oc-body-sm oc-text-secondary" style={{ marginTop: "var(--oc-space-2)" }}>
                        When each unsubscribed contact opted out, in the same formats. Kept apart from
                        the consent date on purpose: an opt-in date on an unsubscribed row would record
                        them leaving on the day they joined.
                        {!mapping.unsubscribed_at && " Without it, they are recorded as opting out today."}
                      </p>
                    </>
                  )}

                  {fields.map((field) => (
                    <MapRow
                      key={field.id}
                      label={field.label}
                      value={mapping.attributes[field.key] ?? ""}
                      headers={headers}
                      onChange={(value) =>
                        setMapping((m) => {
                          const attributes = { ...m.attributes };
                          if (value) attributes[field.key] = value;
                          else delete attributes[field.key];
                          return { ...m, attributes };
                        })
                      }
                    />
                  ))}

                  <MapRow
                    label="Tags"
                    value={mapping.tags[0] ?? ""}
                    headers={headers}
                    onChange={(value) => setMapping((m) => ({ ...m, tags: value ? [value] : [] }))}
                  />
                </section>
              )}

              <section className="drawer-section">
                <span className="oc-label">Consent</span>
                <div className="oc-panel oc-panel--attention">
                  <div className="oc-panel__body oc-body-sm">
                    {mapping.status
                      ? "This is recorded against rows whose status cell is empty, and there is no default. "
                      : "This is recorded against every address in the file, and there is no default. "}
                    Marking a list subscribed when it did not opt in is what turns a mailing into a
                    complaint rate — and the complaint rate is what gets a sending account
                    suspended.
                  </div>
                </div>

                <select
                  className="oc-input"
                  value={consent}
                  onChange={(event) => setConsent(event.target.value as ConsentStatus)}
                >
                  <option value="">Choose…</option>
                  <option value="subscribed">Subscribed — they opted in, and I can show where</option>
                  <option value="pending">Pending — asked, not yet confirmed</option>
                  <option value="unknown">Unknown — nothing was recorded. Campaigns will skip them</option>
                  <option value="unsubscribed">Unsubscribed — this is a suppression list</option>
                </select>
              </section>

              {error && <p className="field-error">{error}</p>}

              <div className="row" style={{ gap: "var(--oc-space-2)" }}>
                <button
                  type="button"
                  className="oc-btn oc-btn--primary"
                  disabled={busy || !file || !consent || !mapping.email}
                  onClick={() => void submit()}
                >
                  {busy ? "Uploading…" : "Start import"}
                </button>
                <button type="button" className="oc-btn" onClick={onClose}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function MapRow({
  label,
  value,
  headers,
  required,
  onChange,
}: {
  label: string;
  value: string;
  headers: string[];
  required?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className="row" style={{ gap: "var(--oc-space-3)" }}>
      <span className="oc-body-sm" style={{ width: 140, flex: "none" }}>
        {label}
        {required && <span className="oc-text-danger"> *</span>}
      </span>
      <select className="oc-input grow" value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">— not imported —</option>
        {headers.map((header) => (
          <option key={header} value={header}>
            {header}
          </option>
        ))}
      </select>
    </div>
  );
}

/** What reading the status column found. */
interface StatusScan {
  reading: boolean;
  values: string[];
  tooMany: boolean;
}

/**
 * More distinct values than this and the column is almost certainly not a
 * status column — somebody picked the email or name column by mistake — so the
 * screen says so rather than rendering a wall of dropdowns.
 */
const MAX_STATUS_VALUES = 20;

const CONSENT_STATUSES: readonly string[] = ["subscribed", "pending", "unknown", "unsubscribed"];

function isConsentStatus(value: string): value is ConsentStatus {
  return CONSENT_STATUSES.includes(value);
}

/**
 * Each value the status column holds, and the consent it records.
 *
 * Nothing is guessed from another platform's words. A value that is already a
 * status name reads as itself, as it does on the server; every other value
 * needs a choice, and one left without is reported as rows that did not import.
 */
function StatusValueMap({
  scan,
  chosen,
  onChoose,
}: {
  scan: StatusScan;
  chosen: Record<string, ConsentStatus>;
  onChoose: (value: string, status: ConsentStatus | "") => void;
}) {
  if (scan.reading) {
    return (
      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        Reading the column…
      </p>
    );
  }
  if (scan.tooMany) {
    return (
      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        This column holds more than {MAX_STATUS_VALUES} different values, so it does not look like a
        status column. Rows whose value is not subscribed, pending, unsubscribed or unknown would be
        reported as not imported.
      </p>
    );
  }
  if (scan.values.length === 0) {
    return (
      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        Every cell in this column is empty, so every row takes the consent chosen below.
      </p>
    );
  }

  return (
    <>
      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        Each value in the column, and the consent it records. A value that is already a status reads as
        itself; any other needs a choice, and one left without is reported as rows that did not import
        rather than guessed at. Empty cells take the consent chosen below.
      </p>
      {scan.values.map((value) => (
        <div className="row" style={{ gap: "var(--oc-space-3)" }} key={value}>
          <span className="oc-mono-sm oc-truncate" style={{ width: 140, flex: "none" }} title={value}>
            {value}
          </span>
          <select
            className="oc-input grow"
            aria-label={`Consent for rows marked ${value}`}
            value={chosen[value] ?? ""}
            onChange={(event) => onChoose(value, event.target.value as ConsentStatus | "")}
          >
            {!isConsentStatus(value.toLowerCase()) && <option value="">— not imported —</option>}
            <option value="subscribed">Subscribed</option>
            <option value="pending">Pending</option>
            <option value="unknown">Unknown</option>
            <option value="unsubscribed">Unsubscribed</option>
          </select>
        </div>
      ))}
    </>
  );
}

/**
 * The distinct values of one column, read from the whole file.
 *
 * A real CSV scan rather than a split on newlines: a quoted cell may hold a
 * newline — a notes column does — and splitting there shifts every later cell
 * of that row into the wrong column. Only the header and the one column's cells
 * are built into strings, so a fifty-megabyte file costs one pass and a handful
 * of values.
 */
function distinctValues(text: string, column: string, limit: number): { values: string[]; tooMany: boolean } {
  const target = normaliseHeaderName(column);
  const seen = new Map<string, string>();
  const header: string[] = [];

  let row = 0;
  let index = 0;
  let targetIndex = -1;
  let cell = "";
  let started = false;
  let quoted = false;

  const keep = () => row === 0 || index === targetIndex;

  const endCell = () => {
    if (row === 0) {
      header.push(cell);
    } else if (index === targetIndex) {
      const value = cell.trim();
      const key = value.toLowerCase();
      if (value && !seen.has(key)) seen.set(key, value);
    }
    cell = "";
    started = false;
    index++;
  };

  const endRow = () => {
    endCell();
    if (row === 0) targetIndex = header.findIndex((name) => normaliseHeaderName(name) === target);
    row++;
    index = 0;
  };

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (quoted) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          if (keep()) cell += '"';
          i++;
        } else {
          quoted = false;
        }
      } else if (keep()) {
        cell += char;
      }
      continue;
    }

    if (char === ",") endCell();
    else if (char === "\n") endRow();
    else if (char === "\r") continue;
    else if (char === '"' && !started) {
      quoted = true;
      started = true;
    } else {
      if (char !== " " && char !== "\t") started = true;
      if (keep()) cell += char;
    }

    if (seen.size > limit) return { values: [...seen.values()].slice(0, limit), tooMany: true };
  }
  if (cell !== "" || index > 0) endRow();

  return { values: [...seen.values()], tooMany: false };
}

/**
 * How the server matches a mapping to a header: case, surrounding space and the
 * byte-order mark Excel writes do not count.
 */
function normaliseHeaderName(name: string): string {
  return name.replace(/^﻿/, "").trim().toLowerCase();
}

/**
 * Polls until the job finishes.
 *
 * Polling rather than a socket: an import takes seconds to minutes, this screen
 * is open for exactly that long, and a websocket would be more machinery than
 * the problem deserves.
 */
function ImportProgress({
  importId,
  onDone,
  onClose,
}: {
  importId: string;
  onDone: () => void;
  onClose: () => void;
}) {
  const [state, setState] = useState<ContactImport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let stopped = false;

    const poll = async () => {
      try {
        const current = await api.get<ContactImport>(`/v1/contacts/imports/${encodeURIComponent(importId)}`);
        if (stopped) return;

        setState(current);
        if (current.status === "completed" || current.status === "failed") {
          onDone();
          return;
        }
        window.setTimeout(() => void poll(), 1000);
      } catch (err) {
        if (!stopped) setError(err instanceof ApiError ? err.message : "Lost track of the import.");
      }
    };

    void poll();
    return () => {
      stopped = true;
    };
  }, [importId, onDone]);

  if (error) return <p className="field-error">{error}</p>;
  if (!state) return <p className="oc-body-sm oc-text-secondary">Starting…</p>;

  const running = state.status === "pending" || state.status === "running";

  return (
    <>
      <ImportOutcome record={state} label={state.filename} />

      {!running && (
        <button type="button" className="oc-btn oc-btn--primary" onClick={onClose}>
          Done
        </button>
      )}
    </>
  );
}

/**
 * What an import did: the dialog shows it when a run finishes, and the Imports
 * tab's drawer shows it the next morning.
 *
 * One rendering, so the two can never describe the same import differently.
 * Until the tab existed the dialog was the only place a skipped row's reason
 * was ever displayed, and it was gone the moment the dialog closed.
 */
export function ImportOutcome({ record, label }: { record: ContactImport; label: string }) {
  const running = record.status === "pending" || record.status === "running";
  const skipped = record.skipped_count ?? 0;
  const reasons = record.errors ?? [];

  return (
    <>
      <section className="drawer-section">
        <span className="oc-label">{label}</span>

        {running && (
          <>
            {/* Indeterminate, and it has to be: the import reports how many rows
                it has created, updated and skipped, and never how many there
                are in total. A bar that invented a percentage would be lying
                about the one thing someone is watching it for. */}
            <Progress label="Importing contacts" />
            <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
              Processing. This runs in the background — closing this panel does not stop it.
            </p>
          </>
        )}

        {record.failure && (
          <div className="oc-panel oc-panel--critical">
            <div className="oc-panel__body oc-body-sm">{record.failure}</div>
          </div>
        )}

        {!running && (
          <div className="row" style={{ gap: "var(--oc-space-6)" }}>
            <Metric label="Created" value={record.created_count ?? 0} />
            <Metric label="Updated" value={record.updated_count ?? 0} />
            <Metric label="Skipped" value={skipped} negative={skipped > 0} />
          </div>
        )}
      </section>

      {reasons.length > 0 && (
        <section className="drawer-section">
          <span className="oc-label">Rows that did not import</span>
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            Everything else was imported. Fix these lines and upload again — re-importing a row that
            already worked updates it rather than duplicating it.
          </p>
          {reasons.length < skipped && (
            <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
              {reasonsNote(reasons.length, skipped)}
            </p>
          )}
          <div className="oc-code" style={{ maxHeight: 260, overflow: "auto", whiteSpace: "pre-wrap" }}>
            {reasons.map((rowError) => `Row ${rowError.row}: ${rowError.message}`).join("\n")}
          </div>
        </section>
      )}
    </>
  );
}

/**
 * Why fewer reasons than skipped rows are listed.
 *
 * The importer records a reason for every row it skips and keeps at most 200 —
 * `maxRecordedErrors` in internal/contacts/importer.go, which a browser cannot
 * read, so change both together. Past that, the list is the first 200. Short of
 * it the importer cannot be the cause, so the sentence states the numbers and
 * does not invent an explanation.
 */
function reasonsNote(shown: number, skipped: number): string {
  if (shown >= 200) {
    return `The first ${shown.toLocaleString()} of ${skipped.toLocaleString()} skipped rows are listed; no more are kept for one import.`;
  }
  return `Reasons were recorded for ${shown.toLocaleString()} of the ${skipped.toLocaleString()} skipped rows.`;
}

/**
 * Splits a CSV header line, honouring quoted fields.
 *
 * A column legitimately named `Address, line 1` is not two columns, and the
 * naive split on commas produces a mapping that is wrong in a way nobody spots
 * until the import puts postcodes in the name field.
 */
function parseHeaderRow(line: string): string[] {
  const out: string[] = [];
  let current = "";
  let quoted = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (quoted) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        current += char;
      }
      continue;
    }

    if (char === '"') quoted = true;
    else if (char === ",") {
      out.push(current.trim());
      current = "";
    } else current += char;
  }

  out.push(current.trim().replace(/^﻿/, ""));
  return out.filter((name) => name !== "");
}
